import React from 'react';
import styled from 'styled-components';

const TrustContainer = styled.section`
  id: trust;
  padding: ${({ theme }) => `${theme.spacing['3xl']} ${theme.spacing.lg}`};
  background: ${({ theme }) => theme.colors.white};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing['2xl']} ${theme.spacing.md}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.sm}`};
  }
`;

const TrustContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const TrustTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary.main};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  line-height: 1.2;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
`;

const TrustSubtitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: 300;
  color: ${({ theme }) => theme.colors.secondary.main};
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const ClientLogos = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: center;
  justify-items: center;
  margin: ${({ theme }) => theme.spacing['2xl']} 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const LogoContainer = styled.div`
  background: ${({ theme }) => theme.colors.accent.gray};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: 8px;
  transition: ${({ theme }) => theme.transitions.smooth};
  width: 200px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.sm};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 150px;
    height: 80px;
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const LogoImage = styled.img`
  max-width: 100%;
  max-height: 60px;
  width: auto;
  height: auto;
  opacity: 0.8;
  filter: grayscale(100%);
  transition: ${({ theme }) => theme.transitions.smooth};

  &:hover {
    opacity: 1;
    filter: grayscale(0%);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-height: 50px;
  }
`;

const AchievementBanner = styled.div`
  background: ${({ theme }) => `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.light} 100%)`};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: 12px;
  margin-top: ${({ theme }) => theme.spacing['2xl']};
  text-align: center;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const AchievementGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const AchievementItem = styled.div`
  text-align: center;
`;

const AchievementNumber = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: 700;
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  line-height: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
  }
`;

const AchievementLabel = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: 300;
  opacity: 0.9;
  line-height: 1.4;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const CredibilityStatement = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.primary.main};
  margin: ${({ theme }) => theme.spacing['2xl']} 0 0 0;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const TrustSignalsSection = () => {
  return (
    <TrustContainer id="trust">
      <TrustContent>
        <TrustTitle>Trusted by Fortune 500 Leaders</TrustTitle>
        <TrustSubtitle>
          Proven results across global enterprises with billions in revenue impact
        </TrustSubtitle>

        <ClientLogos>
          <LogoContainer>
            <LogoImage 
              src="/images/clients/amazon-logo-grayscale.png"
              alt="Amazon - Fortune 500 Client"
            />
          </LogoContainer>
          <LogoContainer>
            <LogoImage 
              src="/images/clients/land-rover-logo-grayscale.png"
              alt="Land Rover - Fortune 500 Client"
            />
          </LogoContainer>
          <LogoContainer>
            <LogoImage 
              src="/images/clients/nestle-logo-grayscale.png"
              alt="Nestlé - Fortune 500 Client"
            />
          </LogoContainer>
          <LogoContainer>
            <LogoImage 
              src="/images/clients/nec-logo-grayscale.png"
              alt="NEC - Fortune 500 Client"
            />
          </LogoContainer>
        </ClientLogos>

        <AchievementBanner>
          <AchievementGrid>
            <AchievementItem>
              <AchievementNumber>40+</AchievementNumber>
              <AchievementLabel>Years Technical Evolution<br />Logic Gates to AI Applications</AchievementLabel>
            </AchievementItem>
            <AchievementItem>
              <AchievementNumber>$5M+</AchievementNumber>
              <AchievementLabel>Revenue Generated<br />Proven Business Model Innovation</AchievementLabel>
            </AchievementItem>
            <AchievementItem>
              <AchievementNumber>100%</AchievementNumber>
              <AchievementLabel>Client Retention<br />Long-term Partnership Success</AchievementLabel>
            </AchievementItem>
          </AchievementGrid>
        </AchievementBanner>

        <CredibilityStatement>
          Multi-dimensional expertise combining Royal Navy systems engineering, 
          Fortune 500 enterprise scaling, and cutting-edge AI implementation for 
          manufacturing transformation.
        </CredibilityStatement>
      </TrustContent>
    </TrustContainer>
  );
};

export default TrustSignalsSection;